import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Mail, MapPin, Phone } from 'lucide-react';
import { profile } from '@/data';
import { Section } from '@/components/layout/Section';
import { Card } from '@/components/ui/Card';
import { Reveal } from '@/components/ui/Reveal';
import { GithubIcon, LinkedinIcon } from '@/components/ui/BrandIcons';

/**
 * The address is shown plainly, as a normal mailto link.
 *
 * An earlier version rendered "user [at] domain" behind a click-to-reveal.
 * That was security theatre: this is a client-rendered site, so the address
 * ships in the JavaScript bundle in clear text regardless — any scraper that
 * fetches the bundle finds it with one regex. The obfuscation stopped nobody
 * and cost a recruiter an extra click before they could copy it, on the one
 * page whose entire job is being easy to contact.
 *
 * The same address is also inside the downloadable resumes served from this
 * site, so treating it as secret here was never coherent.
 */
export function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (permissions, insecure context, older
      // browsers). The mailto link beside this still works, so fail quietly
      // rather than surfacing an error for a convenience feature.
    }
  }, []);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <Section
      id="contact"
      title="Contact"
      lead="Open to backend and cloud engineering roles. Email is the fastest route."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Reveal>
          <Card className="h-full">
            <h3 className="flex items-center gap-2 font-semibold text-foreground">
              <Mail className="h-4 w-4 text-primary" aria-hidden />
              Email
            </h3>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={`mailto:${profile.email}`}
                className="font-mono text-sm break-all text-primary underline underline-offset-4 hover:opacity-80"
              >
                {profile.email}
              </a>

              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
                ) : (
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                )}
                {copied ? 'Copied' : 'Copy'}
              </button>

              {/* Announced to screen readers, which would otherwise get no
                  feedback that the copy succeeded. */}
              <span aria-live="polite" className="sr-only">
                {copied ? 'Email address copied to clipboard' : ''}
              </span>
            </div>

            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" aria-hidden />
              {profile.location}
            </p>

            {/* Phone was previously withheld from the page and shipped only in
                the resume files. Requested on the page so the site and the
                documents agree; the files are downloadable from here anyway,
                so withholding it was never much of a barrier. */}
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="h-4 w-4" aria-hidden />
              <a
                href={`tel:${profile.phone.replace(/[^+\d]/g, '')}`}
                className="hover:text-primary"
              >
                {profile.phone}
              </a>
            </p>
          </Card>
        </Reveal>

        <Reveal index={1}>
          <Card className="h-full">
            <h3 className="font-semibold text-foreground">Elsewhere</h3>
            <div className="mt-3 space-y-2">
              <a
                href={profile.links.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-foreground/90 transition-colors hover:text-primary"
              >
                <LinkedinIcon className="h-4 w-4" />
                {profile.links.linkedin.replace(/^https:\/\/www\./, '')}
              </a>
              <a
                href={profile.links.github}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-foreground/90 transition-colors hover:text-primary"
              >
                <GithubIcon className="h-4 w-4" />
                {profile.links.github.replace(/^https:\/\//, '')}
              </a>
            </div>
          </Card>
        </Reveal>
      </div>
    </Section>
  );
}
